import type { HPOSuggestion } from '@/components/common/hpoTypes'
import { hpoAPIClient } from '@/features/terminology/hpo/HPOAPIClient'
import { extractHPOId } from '@/features/terminology/hpo/hpoTypes'
import * as Sentry from '@sentry/react'
import { useEffect, useRef, useState } from 'react'

const SEARCH_DEBOUNCE_MS = 300

export interface HpoRemoteSearchOptions {
  /** Off while the surface is closed: no request, no results. */
  enabled?: boolean
  minQueryLength?: number
  limit?: number
}

export interface HpoRemoteSearchState {
  /** In API order. */
  results: HPOSuggestion[]
  isSearching: boolean
  /** The last request failed (rate limit, network); `results` is then empty. */
  failed: boolean
}

/** Debounced `GET /terminology/search/hpo` for a typed query; a newer query aborts the one in flight. */
export function useHpoRemoteSearch(
  query: string,
  { enabled = true, minQueryLength = 3, limit = 15 }: HpoRemoteSearchOptions = {}
): HpoRemoteSearchState {
  const [results, setResults] = useState<HPOSuggestion[]>([])
  const [isSearching, setIsSearching] = useState(false)
  const [failed, setFailed] = useState(false)
  const searchRequestIdRef = useRef(0)
  const abortControllerRef = useRef<AbortController | null>(null)

  useEffect(() => {
    const trimmed = query.trim()
    if (!enabled || trimmed.length < minQueryLength) {
      searchRequestIdRef.current += 1
      setResults([])
      setIsSearching(false)
      setFailed(false)
      return
    }

    const requestId = ++searchRequestIdRef.current
    setIsSearching(true)
    // A retry after a failure must not keep showing the old error while it is in flight.
    setFailed(false)

    const timeoutId = setTimeout(async () => {
      abortControllerRef.current?.abort()
      abortControllerRef.current = new AbortController()
      const signal = abortControllerRef.current.signal
      try {
        if (requestId !== searchRequestIdRef.current) return
        const codes = await hpoAPIClient.searchHPO(trimmed, limit, signal)
        if (requestId !== searchRequestIdRef.current) return
        setResults(
          codes.map((c) => ({
            id: extractHPOId(c.code),
            name: c.description,
            hpoCode: extractHPOId(c.code),
          }))
        )
        setFailed(false)
      } catch (error) {
        if (requestId === searchRequestIdRef.current) {
          setResults([])
          if ((error as Error).name !== 'AbortError') {
            setFailed(true)
            Sentry.captureException(error, { tags: { operation: 'hpo.search' } })
          }
        }
      } finally {
        if (requestId === searchRequestIdRef.current) {
          setIsSearching(false)
        }
      }
    }, SEARCH_DEBOUNCE_MS)

    return () => {
      clearTimeout(timeoutId)
      abortControllerRef.current?.abort()
    }
  }, [enabled, query, minQueryLength, limit])

  return { results, isSearching, failed }
}

/**
 * Stable re-rank of API results: an exact name match first, then names starting with the query,
 * then the rest in API order. The API ranks semantically and can put the exact term several rows down.
 */
export function rankHpoResults(query: string, results: readonly HPOSuggestion[]): HPOSuggestion[] {
  const needle = query.trim().toLowerCase()
  if (!needle) return [...results]
  const tier = (term: HPOSuggestion) => {
    const name = term.name.toLowerCase()
    if (name === needle || term.hpoCode.toLowerCase() === needle) return 0
    if (name.startsWith(needle)) return 1
    return 2
  }
  return results
    .map((term, index) => ({ term, index, tier: tier(term) }))
    .sort((a, b) => a.tier - b.tier || a.index - b.index)
    .map(({ term }) => term)
}
